import { IonItem, IonItemOption, IonItemOptions, IonItemSliding } from '@ionic/react';
import StatusTracker from 'components/StatusTracker';
import { useStatus } from 'hooks/useStatus';
import { useWorkOrderTracking } from 'hooks/useWorkOrderTracking';
import { WorkOrder } from 'interfaces/WorkOrder';
import moment from 'moment';
import React, { RefObject, useRef } from 'react';
import { Link } from 'react-router-dom';

import { useStyles } from './styles';

const formatShortName = (fullName: string) => {
  const parts = fullName.trim().split(/\s+/);
  if (parts.length < 2) return fullName;

  const firstName = parts[0];
  const lastInitial = parts[parts.length - 1].charAt(0);

  return `${firstName} ${lastInitial}.`;
};

const formatCardDate = (time: number) => {
  const now = moment();

  if (now.diff(time) > 604800000) {
    return moment(time).format('MMM D');
  }

  const hours = now.diff(time, 'hours');

  return hours < 24 ? `${hours}h` : `${now.diff(time, 'days')}d`;
};

interface Props
  extends Pick<
    WorkOrder,
    'id' | 'status' | 'titleDisplay' | 'reason' | 'createdAt' | 'isResidentRequest'
  > {
  category?: Pick<NonNullable<WorkOrder['category']>, 'name'>;
  location?: Pick<NonNullable<WorkOrder['location']>, 'address'>;
  assignee?: Pick<NonNullable<WorkOrder['assignee']>, 'name'>;
  comments?: Pick<NonNullable<WorkOrder['comments']>, 'length'>;
  disableSliding?: boolean;
  pageRef?: RefObject<HTMLElement>;
}

const Component: React.FC<Props> = ({
  id,
  category,
  status,
  titleDisplay: title,
  location,
  assignee,
  createdAt,
  isResidentRequest,
  comments,
  reason,
  disableSliding = false,
  pageRef,
}) => {
  const locationAddress = location?.address ?? 'No Location';
  const commentCount = comments?.length ?? 0;

  const { label: formattedStatus, color: statusColor } = useStatus(status);

  const classes = useStyles({ statusColor });

  const formattedDate = formatCardDate(createdAt);

  const itemSlidingRef = useRef<HTMLIonItemSlidingElement>(null);

  const { changeStatus } = useWorkOrderTracking({ workorderId: id });

  const slideAction = (callback: () => void) => {
    callback();
    if (itemSlidingRef.current) {
      itemSlidingRef.current.close();
    }
  };

  return (
    <IonItemSliding ref={itemSlidingRef} disabled={disableSliding} className={classes.slider}>
      <StatusTracker
        pageRef={pageRef}
        onStatusChange={changeStatus}
        status={status}
        renderStatusItem={({ onClick }) => (
          <IonItemOptions side="start" onIonSwipe={() => slideAction(onClick)}>
            <IonItemOption
              style={{ '--background': statusColor }}
              expandable={true}
              onClick={() => slideAction(onClick)}
            >
              Update Status
            </IonItemOption>
          </IonItemOptions>
        )}
      />
      <IonItem lines="none" className={classes.item}>
        <Link
          className={classes.card}
          aria-label={`View details for ${title}`}
          to={`/workorders/details/${id}`}
          draggable={false}
        >
          {/*
           *
           * Card Header
           */}
          <div className={classes.header}>
            <div className={classes.status}>{formattedStatus}</div>
            {category?.name && <span className={classes.category}>{category.name}</span>}
          </div>
          <div className={classes.content}>
            {/*
             *
             * Card Body
             */}
            <div className={classes.body}>
              <h1 className={classes.title}>{title}</h1>
              <p className={classes.location}>{locationAddress}</p>
              {/*
               *
               * Status Callout
               */}
              {reason && (
                <div className={classes.statusCallout}>
                  <p className={classes.calloutValue}>{`"${reason}"`}</p>
                </div>
              )}
            </div>
            {/*
             *
             * Card Footer
             */}
            <div className={classes.footer}>
              <span className={classes.assignee}>
                {assignee?.name ? formatShortName(assignee.name) : 'Unassigned'}
              </span>
              <span className={classes.createdDate}>{formattedDate}</span>
              <span className={classes.footerRightGroup}>
                {commentCount > 0 && (
                  <span className={classes.comments}>
                    {commentCount} comment{commentCount === 1 ? '' : 's'}
                  </span>
                )}
                {isResidentRequest && <span className={classes.resident}>Resident</span>}
              </span>
            </div>
          </div>
        </Link>
      </IonItem>
    </IonItemSliding>
  );
};

export default Component;
